import { type MetaFunction } from "react-router"
import { type LoaderFunctionArgs, data } from "react-router"
import { Link, useLoaderData } from "react-router"
import BlogImage from "#app/components/blog-image.tsx"
import { NewsletterForm } from "#app/components/newsletter-form.tsx"
import { APP_NAME, DEFAULT_BLOG_AUTHOR } from "#app/utils/constants"
import { generateMeta } from "#app/utils/meta"
import { prisma } from "#app/utils/db.server.ts"
import { getBlogImgSrc } from "#app/utils/misc.tsx"

export async function loader({ request }: LoaderFunctionArgs) {
	const posts = await prisma.post.findMany({
		where: {
			published: true,
		},
		select: {
			id: true,
			title: true,
			slug: true,
			excerpt: true,
			publishedAt: true,
			images: {
				select: {
					id: true,
					altText: true,
				},
				take: 1,
			},
			author: {
				select: {
					name: true,
				},
			},
		},
		orderBy: { publishedAt: "desc" },
	})

	const postsWithImages = posts.map(post => ({
		...post,
		imageId: post.images[0]?.id ?? null,
	}))

	return data({ posts: postsWithImages })
}

export const meta: MetaFunction = () => {
	return generateMeta({
		title: `Blog - ${APP_NAME}`,
		description:
			"Lee nuestras últimas noticias y artículos sobre patchwork y artesanías.",
		canonical: "/blog",
	})
}

export default function BlogIndex() {
	const { posts } = useLoaderData<typeof loader>()

	return (
		<div className="container mx-auto px-4 py-16">
			<header className="mb-16 text-center">
				<h1 className="font-script text-5xl text-primary dark:text-primary-foreground">
					Blog
				</h1>
				<p className="mt-4 text-lg text-muted-foreground dark:text-foreground/70">
					Descubre nuestras últimas noticias, consejos y artículos sobre el
					mundo del patchwork.
				</p>
			</header>

			{posts.length > 0 ? (
				<div className="grid gap-10 md:grid-cols-2">
					{posts.map((post, index) => (
						<article
							key={post.id}
							className="group relative flex flex-col overflow-hidden rounded-xl bg-card shadow-sm transition-all duration-300 hover:shadow-lg dark:bg-card/50"
						>
							{post.imageId ? (
								<Link
									to={`/blog/${post.slug}`}
									className="relative aspect-[16/9] overflow-hidden"
								>
									<BlogImage
										src={getBlogImgSrc(post.imageId)}
										alt={post.images[0]?.altText ?? post.title}
										className="transition-transform duration-500 group-hover:scale-105"
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
								</Link>
							) : (
								<Link
									to={`/blog/${post.slug}`}
									className="relative aspect-[16/9] overflow-hidden bg-secondary dark:bg-secondary/20"
								>
									<div className="flex h-full items-center justify-center">
										<svg
											className="h-16 w-16 text-muted-foreground/30"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1}
												d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
											/>
										</svg>
									</div>
								</Link>
							)}

							<div className="flex flex-1 flex-col p-6">
								<div className="mb-3 flex items-center gap-3 text-sm text-muted-foreground dark:text-foreground/60">
									{post.publishedAt && (
										<time
											dateTime={post.publishedAt.toISOString()}
											className="flex items-center gap-1"
										>
											<svg
												className="h-4 w-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
												/>
											</svg>
											{new Date(post.publishedAt).toLocaleDateString("es-ES", {
												day: "numeric",
												month: "short",
											})}
										</time>
									)}
									<span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
									<span>{post.author?.name ?? DEFAULT_BLOG_AUTHOR}</span>
								</div>

								<Link to={`/blog/${post.slug}`} className="group/title">
									<h2 className="font-serif text-2xl font-medium leading-tight text-foreground transition-colors group-hover/title:text-primary dark:group-hover/title:text-primary-foreground">
										{post.title}
									</h2>
								</Link>

								{post.excerpt && (
									<p className="mt-3 line-clamp-3 text-muted-foreground dark:text-foreground/70">
										{post.excerpt}
									</p>
								)}

								<div className="mt-auto flex items-center pt-4">
									<Link
										to={`/blog/${post.slug}`}
										className="group/link inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80 dark:text-primary-foreground"
									>
										<span>Leer artículo</span>
										<svg
											className="h-4 w-4 transition-transform group-hover/link:translate-x-1"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M17 8l4 4m0 0l-4 4m4-4H3"
											/>
										</svg>
									</Link>
								</div>
							</div>
						</article>
					))}
				</div>
			) : (
				<div className="py-16 text-center">
					<p className="text-muted-foreground dark:text-foreground/70">
						Próximamente publicaremos nuevo contenido en nuestro blog.
					</p>
				</div>
			)}

			<div className="mt-16">
				<NewsletterForm variant="prominent" listType="blog" />
			</div>
		</div>
	)
}
